import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { EllipsesMenu, EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import { NavigationDataItem } from 'src/components/Navbar/use-navigation-items';
import { useNavigateRoute } from 'src/utils/routing';
import { NavigationItem } from './navigation-item';
import { isPathActive } from './utils';

interface DraggableNavItemProps {
  id: string;
  item: NavigationDataItem;
  sectionId: string;
  onClick?: (item: NavigationDataItem) => void;
  menuItems?: EllipsesMenuOption[];
}

export const DraggableNavItem = ({
  id,
  item,
  sectionId,
  onClick,
  menuItems,
}: DraggableNavItemProps) => {
  const { navigate, pathnameWithoutPrefix } = useNavigateRoute();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable(
    {
      id,
      data: { item, sectionId },
    },
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isActive = useMemo(
    () =>
      isPathActive({
        pathTemplate: item.pathTemplate,
        path: item.path,
        locationPath: pathnameWithoutPrefix,
      }),
    [item.path, item.pathTemplate, pathnameWithoutPrefix],
  );

  const handleClick = (e: React.MouseEvent) => {
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    onClick?.(item);
    if (item.path && item.path !== location.pathname) {
      navigate(item.path);
    }
  };

  return (
    <Box ref={setNodeRef} sx={style} {...attributes} {...listeners}>
      <NavigationItem
        item={item}
        onClick={handleClick}
        isActive={isActive}
        isAdornmentPinned={isMenuOpen}
        rightAdornment={
          menuItems?.length ? (
            <V2ThemeProvider>
              <EllipsesMenu
                icon="more-vertical"
                iconLibrary="lucide"
                options={menuItems}
                onOpenChange={setIsMenuOpen}
              />
            </V2ThemeProvider>
          ) : undefined
        }
      />
    </Box>
  );
};
